import { refreshOidcToken } from '@/api/auth';
import { startSmCookieRefresh, stopSmCookieRefresh } from '@/composables/useSmCookieRefresh';
import { ACCESS_TOKEN } from '@/types';
import { clearTokens, isOidcSession, setAccessToken } from '@/utils/helpers';

const REFRESH_BEFORE_EXPIRY_MS = 60_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const clearRefreshTimer = () => {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const getTokenExpMs = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const redirectToWelcomeBack = () => {
  clearRefreshTimer();
  stopSmCookieRefresh();
  clearTokens();
  window.location.href = '/v2/welcome-back/';
};

const scheduleNextRefresh = (): void => {
  if (!isOidcSession()) return;

  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  if (!accessToken) return;

  const expMs = getTokenExpMs(accessToken);
  if (expMs === null) return;

  const msUntilRefresh = expMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

  clearRefreshTimer();

  const performRefresh = async () => {
    if (!isOidcSession()) {
      clearRefreshTimer();
      return;
    }
    const { data, error } = await refreshOidcToken();
    // Re-check after the async call: logout may have run while the request was in-flight.
    if (!isOidcSession()) {
      clearRefreshTimer();
      return;
    }
    if (error || !data?.access_token) {
      redirectToWelcomeBack();
      return;
    }
    setAccessToken(data.access_token);
    scheduleNextRefresh();
  };

  const handleUnexpectedError = () => {
    if (isOidcSession()) {
      redirectToWelcomeBack();
    }
  };

  if (msUntilRefresh <= 0) {
    performRefresh().catch(handleUnexpectedError);
    return;
  }

  refreshTimer = setTimeout(() => {
    performRefresh().catch(handleUnexpectedError);
  }, msUntilRefresh);
};

export const scheduleOidcRefresh = (): void => {
  scheduleNextRefresh();
  startSmCookieRefresh();
};

export const cancelOidcRefresh = (): void => {
  clearRefreshTimer();
  stopSmCookieRefresh();
};
